import { useMemo, useRef } from 'react';
import type { Permission } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, PageHeader } from '../components/ui.js';
import { HubGraph, HubTools, useHubView, type HubControls } from '../components/HubGraph.js';

// ── workspace map (an interconnected web of the main areas) ─────────────────────
// This is the app's home: a 3D "spider web" of the areas the signed-in user can reach, so a new
// or occasional user can find their way around at a glance. Items and cross-links are filtered to
// what the user has permission to see.
// (`perm`: one permission, or any one of several)
const MAP: Array<{ label: string; to: string; perm: Permission | readonly Permission[]; group: string }> = [
  { label: 'Schedule', to: '/schedule', perm: 'schedule:read', group: 'Management' },
  { label: 'Overview', to: '/overview', perm: 'overview:read', group: 'Management' },
  { label: 'Pre-Delivery Alerts', to: '/assembly-alerts', perm: 'overview:read', group: 'Management' },
  { label: 'Engine Orders', to: '/engine-orders', perm: 'overview:read', group: 'Management' },
  { label: 'Washing Plan', to: '/washing', perm: 'overview:read', group: 'Management' },
  { label: 'Assembly Board', to: '/board', perm: 'board:read', group: 'Management' },
  { label: 'Assembly Reporting', to: '/reporting', perm: 'report:read', group: 'Management' },
  { label: 'Deck Flooring', to: '/deck', perm: 'overview:read', group: 'Management' },
  { label: 'Employees', to: '/employees', perm: 'production:read', group: 'Management' },

  { label: 'Assembly Progress', to: '/assembly', perm: 'production:read', group: 'Assembly' },
  { label: 'PDI', to: '/pdi', perm: 'pdi:read', group: 'Assembly' },
  { label: 'DS - Programming', to: '/ds', perm: 'ds:read', group: 'Assembly' },
  { label: 'Loading', to: '/loading', perm: 'overview:read', group: 'Assembly' },
  { label: 'Labels', to: '/labels', perm: 'labels:read', group: 'Assembly' },

  { label: 'GRP Inspection', to: '/grp', perm: 'grp:read', group: 'GRP' },

  { label: 'Service', to: '/service', perm: 'service:read', group: 'Service' },
  { label: 'Service Requests', to: '/service-requests', perm: 'servicereq:read', group: 'Service' },

  { label: 'Customer Database', to: '/sales/customers', perm: 'customer_database.view', group: 'Sales' },
  { label: 'Order Sheets', to: '/sales/sheets', perm: 'sales:read', group: 'Sales' },
  { label: 'Change Log', to: '/sales/log', perm: 'sales:read', group: 'Sales' },
  { label: 'Vessel Progress', to: '/vessel-progress', perm: ['overview:read', 'sales:read'], group: 'Sales' },

  { label: 'Vessel Status', to: '/vessel-status', perm: 'vesselstatus:read', group: 'Warehouse' },
  { label: 'Fuel Tank S/N', to: '/fuel-tank-sn', perm: 'vesselstatus:read', group: 'Warehouse' },
  { label: 'Order Sheet Rollup', to: '/order-rollup', perm: 'production:read', group: 'Warehouse' },

  { label: 'Vessel ID', to: '/vessel-identification', perm: 'hin:read', group: 'Records' },
  { label: 'Agenda', to: '/agenda', perm: 'agenda:read', group: 'Records' },
];
// semantic cross-links between related areas — the web strands that tie the clusters together.
const CROSS: Array<[string, string]> = [
  ['Schedule', 'Overview'],
  ['Overview', 'Assembly Board'],
  ['Overview', 'Pre-Delivery Alerts'],
  ['Overview', 'Engine Orders'],
  ['Overview', 'Washing Plan'],
  ['Service', 'Washing Plan'],
  ['Schedule', 'Pre-Delivery Alerts'],
  ['Assembly Board', 'Assembly Progress'],
  ['Assembly Board', 'Assembly Reporting'],
  ['Overview', 'GRP Inspection'],
  ['Overview', 'Vessel ID'],
  ['Overview', 'Vessel Status'],
  ['Assembly Progress', 'PDI'],
  ['PDI', 'DS - Programming'],
  ['Deck Flooring', 'Loading'],
  ['Customer Database', 'Order Sheets'],
  ['Order Sheets', 'Overview'],
  ['Service', 'Service Requests'],
  ['Vessel Status', 'Fuel Tank S/N'],
];

export function Explore() {
  const { me, can, canSeePath } = useAuth();
  const controls = useRef<HubControls | null>(null);
  const [view, setView] = useHubView();
  const { items, links } = useMemo(() => {
    const nodes = MAP.filter((l) => (typeof l.perm === 'string' ? can(l.perm) : l.perm.some((p) => can(p))) && canSeePath(l.to));
    const present = new Set(nodes.map((n) => n.label));
    return {
      items: nodes.map((n) => ({ label: n.label, path: n.to, group: n.group })),
      links: CROSS.filter(([a, b]) => present.has(a) && present.has(b)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, canSeePath]);

  if (items.length === 0) {
    return (
      <>
        <PageHeader title={`Welcome, ${me?.user.displayName ?? ''}`} />
        <EmptyState title="Nothing to explore yet" hint="You don't have access to any areas. Ask an administrator for a role." />
      </>
    );
  }

  // Full-bleed, box-less 3D map with a quiet floating glass control cluster (deference: chrome
  // recedes, the graph is the content). Drag to orbit, scroll or pinch to zoom, flick for momentum.
  // Map | List: the same pages as plain rows, which is where a phone opens until the user picks Map.
  return (
    <HubGraph fullBleed center="Rolodex" items={items} links={links} controlsRef={controls} view={view}>
      <div className="explore-ui">
        <h1 className="explore-greeting ex-glass">Welcome, {me?.user.displayName ?? ''}</h1>
        <HubTools controls={controls} view={view} onView={setView} />
      </div>
    </HubGraph>
  );
}
